import {lineAnchorFrom, lineOrientationFrom} from '@github-ui/diffs/diff-line-helpers'
import type {DiffAnchor} from '@github-ui/diffs/types'
import type {PropsWithChildren} from 'react'
import React, {useCallback, useRef} from 'react'
import {InlineCommentDialogModeProvider} from '@github-ui/conversations/inline-comment-dialog-mode-context'

import {ContentCell, EmptyCell, LineNumberCell} from './DiffLineTableCellParts'
import {MarkersDialogContextProvider} from '../contexts/MarkersDialogContext'
import {useDiffContext} from '../contexts/DiffContext'
import {useDiffLineContext} from '../contexts/DiffLineContext'
import {isEmptyDiffLine} from '../helpers/line-helpers'
import type {FileDiffReference, DiffLine, ClientDiffLine} from '../types'
import {useMarkersDialogStatus} from '../hooks/use-markers-dialog-status'
import {HiddenUnicodeAlert} from '@github-ui/hidden-unicode-banner/HiddenUnicodeAlert'

const CodeDiffLineUnmemoized = ({
  diffLine,
  handleDiffSideCellSelectionBlocking,
  isRowSelected,
  lineAnchor,
  filePath,
  copilotChatReference,
}: {
  diffLine: ClientDiffLine
  filePath: string
  lineAnchor: string
  handleDiffSideCellSelectionBlocking?: (diffSide: 'left' | 'right') => void
  isRowSelected: boolean
  copilotChatReference?: FileDiffReference
}) => {
  const {viewerData} = useDiffContext()
  const {hasHiddenUnicodeCharacters, isLeftSide, isSplit} = useDiffLineContext()
  const contentRef = useRef<HTMLTableCellElement>(null)
  const line = diffLine as DiffLine

  /*
   * display the left cell for all lines in a unified view, and left lines of a split view
   * this cell will not contain a number in the unified view if the line is an addition
   */
  const showLeftNumber = line.type !== 'ADDITION'
  const showLeftNumberCell = !isSplit || isLeftSide

  /*
   * display the right cell for all lines in a unified view, and right lines of a split view
   * this cell will not contain a number in the unified view if the line is a deletion
   */
  const showRightNumber = line.type !== 'DELETION'
  const showRightNumberCell = !isSplit || !isLeftSide

  const handleRightDiffSideCellSelectionBlocking = useCallback(() => {
    if (!handleDiffSideCellSelectionBlocking) return
    const diffSideToBlock = 'left'
    handleDiffSideCellSelectionBlocking(diffSideToBlock)
  }, [handleDiffSideCellSelectionBlocking])

  const handleLeftDiffSideCellSelectionBlocking = useCallback(() => {
    if (!handleDiffSideCellSelectionBlocking) return
    const diffSideToBlock = 'right'
    handleDiffSideCellSelectionBlocking(diffSideToBlock)
  }, [handleDiffSideCellSelectionBlocking])

  const contentColumnIndex = isSplit ? (isLeftSide ? 1 : 3) : 2

  /**
   * The `markersStatus` state tracks whether the user has manually expanded/minimized
   * inline comment markers in the diff view while the "Collapsed" CommentsPreference setting is enabled
   * This state is critical for preserving a mouse user's intent regarding the visibility of
   * inline markers during their interaction with the diff view while in CommentsPreference is Collapsed.
   */
  const [markersStatus, dispatchMarkersStatus] = useMarkersDialogStatus(viewerData.commentsPreference)

  return (
    <MarkersDialogContextProvider line={line}>
      <InlineCommentDialogModeProvider>
        {showLeftNumberCell && (
          <LineNumberCell
            copilotChatReference={copilotChatReference}
            columnIndex={0}
            contentColumnIndex={contentColumnIndex}
            diffLine={diffLine}
            filePath={filePath}
            handleDiffSideCellSelectionBlocking={handleLeftDiffSideCellSelectionBlocking}
            isRowSelected={isRowSelected}
            contentRef={contentRef}
            dispatchMarkersStatus={dispatchMarkersStatus}
          >
            {hasHiddenUnicodeCharacters && !showRightNumber && (
              <DiffCodeAlert>
                <HiddenUnicodeAlert />
              </DiffCodeAlert>
            )}
            {showLeftNumber && line.left}
          </LineNumberCell>
        )}
        {showRightNumberCell && (
          <LineNumberCell
            copilotChatReference={copilotChatReference}
            columnIndex={isSplit ? 2 : 1}
            contentColumnIndex={contentColumnIndex}
            diffLine={diffLine}
            filePath={filePath}
            handleDiffSideCellSelectionBlocking={handleRightDiffSideCellSelectionBlocking}
            isRowSelected={isRowSelected}
            contentRef={contentRef}
            dispatchMarkersStatus={dispatchMarkersStatus}
          >
            {hasHiddenUnicodeCharacters && showRightNumber && (
              <DiffCodeAlert>
                <HiddenUnicodeAlert />
              </DiffCodeAlert>
            )}
            {showRightNumber && line.right}
          </LineNumberCell>
        )}
        <ContentCell
          copilotChatReference={copilotChatReference}
          columnIndex={contentColumnIndex}
          diffLine={diffLine}
          filePath={filePath}
          lineAnchor={lineAnchor}
          handleDiffSideCellSelectionBlocking={
            isLeftSide ? handleLeftDiffSideCellSelectionBlocking : handleRightDiffSideCellSelectionBlocking
          }
          isRowSelected={isRowSelected}
          ref={contentRef}
          markersStatus={markersStatus}
          dispatchMarkersStatus={dispatchMarkersStatus}
        />
      </InlineCommentDialogModeProvider>
    </MarkersDialogContextProvider>
  )
}

interface DiffSideProps {
  diffLine: ClientDiffLine
  fileAnchor: DiffAnchor
  filePath: string
  handleDiffSideCellSelectionBlocking: (diffSide: 'left' | 'right') => void
  isRowSelected: boolean
  copilotChatReference?: FileDiffReference
}

export const DiffLineSideUnmemoized = ({
  diffLine,
  fileAnchor,
  filePath,
  handleDiffSideCellSelectionBlocking,
  isRowSelected,
  copilotChatReference,
}: DiffSideProps) => {
  const {isLeftSide} = useDiffLineContext()
  if (isEmptyDiffLine(diffLine)) {
    return (
      <>
        <EmptyCell columnIndex={isLeftSide ? 0 : 2} diffLine={diffLine} />
        <EmptyCell columnIndex={isLeftSide ? 1 : 3} showRightBorder={isLeftSide} diffLine={diffLine} />
      </>
    )
  }

  const lineOrientation = isLeftSide ? 'left' : lineOrientationFrom(diffLine.type)
  const lineNumber = lineOrientation === 'left' ? diffLine.left : diffLine.right
  const lineAnchor = lineAnchorFrom(fileAnchor, lineOrientation, lineNumber ?? 0)

  switch (diffLine.type) {
    case 'HUNK':
      return null
    case 'ADDITION':
    case 'DELETION':
    case 'CONTEXT':
    case 'INJECTED_CONTEXT':
      return (
        <CodeDiffLine
          copilotChatReference={copilotChatReference}
          diffLine={diffLine}
          filePath={filePath}
          handleDiffSideCellSelectionBlocking={handleDiffSideCellSelectionBlocking}
          isRowSelected={isRowSelected}
          lineAnchor={lineAnchor}
        />
      )
    default:
      throw new Error(`cannot handle type ${diffLine.type}`)
  }
}

function DiffCodeAlert({children}: PropsWithChildren) {
  return (
    <span className="position-absolute" style={{left: '-1px', zIndex: '1'}}>
      {children}
    </span>
  )
}

export const DiffLineSide = React.memo(DiffLineSideUnmemoized)
export const CodeDiffLine = React.memo(CodeDiffLineUnmemoized)

try{ CodeDiffLineUnmemoized.displayName ||= 'CodeDiffLineUnmemoized' } catch {}
try{ DiffLineSideUnmemoized.displayName ||= 'DiffLineSideUnmemoized' } catch {}
try{ DiffCodeAlert.displayName ||= 'DiffCodeAlert' } catch {}
try{ DiffLineSide.displayName ||= 'DiffLineSide' } catch {}
try{ CodeDiffLine.displayName ||= 'CodeDiffLine' } catch {}